import { escapeHtml } from '../../../utils/escapeHtml';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import './LineChart.scss';
import type { ECharts, EChartsOption, SeriesOption } from 'echarts';
import { patchPanelSetting } from '../../../services/user/UserService';
import {
  formatLargeNumber,
  SELECTED_BRAND_COLOR,
  normalizeKeyword,
} from '../../../utils/utils';
import { useTranslation } from 'react-i18next';
import { MonitorEventsResponse } from '../../../models/monitor/MonitorEventsResponse';
import { parseISO, startOfDay, endOfDay } from 'date-fns';
import { TagsResponse } from '../../../models/tags/TagsResponse';
import { KeywordResponse } from '../../../models/monitor/KeywordResponse';
import { findTagIdInKeyword } from '../../../helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../helpers/tags/TagsHelper';
import { getChartThemeColors, getFirstSeriesPalette, getSecondSeriesPalette } from '../../../utils/chartTheme';
import { useTheme } from '../../../contexts/theme/ThemeContext';

export type LineChartEffectScatterPoint = {
  date: string;
  value: number;
  extra?: Record<string, unknown>;
};

interface LineChartProps {
  /** Wystawia instancję ECharts na zewnątrz — raport zrzuca z niej wykres do PDF-a. */
  chartRef?: React.RefObject<ReactECharts>;
  firstData: (string | number)[][];
  firstColorPalette?: string[];
  secondData?: (string | number)[][];
  secondColorPalette?: string[];
  colorsByName?: { name: string; color: string }[];
  headers?: string[];
  onLegendClick?: (legendStateMap: Map<string, boolean>) => void;
  showOutside30?: boolean;
  legendType?: 'SPACE_BETWEEN' | 'FLEX_START';
  labelSecondY?: string;
  inverseY?: boolean;
  inverseSecondY?: boolean;
  selectedBrand?: string;
  marksData?: MonitorEventsResponse[] | null;
  keywordsMapper?: Map<string, string>;
  tags?: TagsResponse[];
  commonKeywords?: KeywordResponse[];
  autoFitYAxis?: boolean;
  minValue?: number;
  maxValue?: number;
  isMax30?: boolean;
  formatYAsPercentage?: boolean;
  chartSizeStyle?: { width: string; height: string };
  panelSettingsConfig?: string;
  prefetchedDisplayMarkers?: boolean;
  smooth?: boolean;
  legendPosition?: 'top' | 'bottom';
  legendSize?: 'default' | 'small';
  firstDataEffectScatterPoints?: LineChartEffectScatterPoint[];
  secondDataEffectScatterPoints?: LineChartEffectScatterPoint[];
  /** Formats the raw first-column value (ISO key) into a compact X-axis label. */
  dateAxisFormatter?: (rawLabel: string) => string;
  /** Formats the raw first-column value (ISO key) into the tooltip date label. */
  dateTooltipFormatter?: (rawLabel: string) => string;
  effectScatterTooltipFormatter?: (params: any) => string;
  /** When set, the second (right) Y axis still auto-scales but never exceeds this max (e.g. 100 for %). */
  secondAxisMax?: number;
  /**
   * When false, the second (right) Y axis renders raw numeric labels instead of `%`
   * (e.g. for an average-position series). Defaults to true to preserve existing callers.
   */
  formatSecondYAsPercentage?: boolean;
  /**
   * Series names (matrix header labels) that start **deselected** in the legend — the series is
   * present in the legend but its line is hidden until the user clicks the legend item. Defaults
   * to none. The custom legend item renders inactive (greyed) and ECharts hides the line via
   * `legend.selected`.
   */
  initiallyHiddenSeries?: string[];
  /**
   * When true, the legend lists the `secondData` series group BEFORE the `firstData` group. Axis
   * mapping and series order are unchanged — only the legend's visual order flips. Defaults to false.
   * Useful when a metric must live on the (left) `firstData` axis but appear last in the legend.
   */
  secondDataLegendFirst?: boolean;
  /** Called when the ECharts instance is ready and after layout-affecting updates. */
  onChartReady?: (chart: ECharts) => void;
  /**
   * Opt-in: render a custom icon (e.g. a brand logo/initial badge) instead of the plain colored
   * dot in each legend item. When omitted, the default `line-chart__legend-color` dot is used.
   */
  renderLegendIcon?: (label: string, color?: string) => React.ReactNode;
  /** Opt-in: capitalize brand names in the legend and the tooltip (display only). */
  capitalizeBrands?: boolean;
  /** Rendered inside the chart box (e.g. X-axis overlays). */
  children?: React.ReactNode;
}

type GroupedMarkLineData = {
  xAxis: string;
  events: MonitorEventsResponse[];
};

const LineChart: React.FC<LineChartProps> = ({
  firstData,
  firstColorPalette = getFirstSeriesPalette(),
  secondData,
  secondColorPalette = getSecondSeriesPalette(),
  colorsByName,
  headers,
  onLegendClick,
  showOutside30 = false,
  inverseY = false,
  legendType = 'SPACE_BETWEEN',
  inverseSecondY,
  selectedBrand,
  marksData,
  keywordsMapper,
  tags,
  commonKeywords,
  autoFitYAxis = false,
  minValue = 1,
  maxValue,
  isMax30 = true,
  formatYAsPercentage = false,
  chartSizeStyle = { width: '100%', height: '294px' },
  panelSettingsConfig,
  prefetchedDisplayMarkers,
  smooth = false,
  legendPosition = 'top',
  legendSize = 'default',
  firstDataEffectScatterPoints,
  secondDataEffectScatterPoints,
  dateAxisFormatter,
  dateTooltipFormatter,
  effectScatterTooltipFormatter,
  secondAxisMax,
  formatSecondYAsPercentage = true,
  initiallyHiddenSeries,
  secondDataLegendFirst = false,
  onChartReady,
  renderLegendIcon,
  capitalizeBrands = false,
  children,
  chartRef,
}) => {
  const eChartsRef = useRef<ReactECharts>(null);
  useImperativeHandle(chartRef, () => eChartsRef.current!, []);
  const isOverScatterRef = useRef(false);
  const scatterTooltipParamsRef = useRef<any>(null);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const hasPrefetchedDisplayMarkers = typeof prefetchedDisplayMarkers === 'boolean';
  const [showMarkers, setShowMarkers] = useState<boolean>(
    hasPrefetchedDisplayMarkers ? prefetchedDisplayMarkers : !panelSettingsConfig
  );
  const hasMarksData = Boolean(marksData && marksData.length > 0);
  const activeMarksData = hasMarksData && showMarkers ? marksData : null;

  useEffect(() => {
    if (!hasPrefetchedDisplayMarkers) {
      return;
    }

    setShowMarkers((prev) => (prev === prefetchedDisplayMarkers ? prev : prefetchedDisplayMarkers));
  }, [hasPrefetchedDisplayMarkers, prefetchedDisplayMarkers]);

  const toggleMarkers = async (): Promise<void> => {
    const nextValue = !showMarkers;
    setShowMarkers(nextValue);

    if (!panelSettingsConfig) {
      return;
    }

    try {
      await patchPanelSetting('charts', panelSettingsConfig, { displayMarkers: nextValue });
    } catch (_error) {
      // Keep optimistic behavior for temporary non-persistent backend endpoint.
    }
  };
  const renderEventMarkHtml = (event: MonitorEventsResponse): string => {
    const iconHtml = event.iconClass ? `<span class="ico ${event.iconClass} ico--xs"></span>` : '';
    const modelHtml = event.model
      ? `<span class="line-chart-event__chat-type"><span class="ico ${event.model.iconClass} ico--xs"></span><span>${escapeHtml(event.model.label)}</span></span>`
      : '';

    return `
      <span class="line-chart-event">
        ${iconHtml}
        <span class="line-chart-event__label">${escapeHtml(event.label)}</span>
        ${modelHtml}
      </span>
    `;
  };

  const getLineSeries = (
    lineChartData: (string | number)[][] | undefined,
    lineChartSecondData: (string | number)[][] | undefined
  ): Object[] => {
    if (!lineChartData && !lineChartSecondData) {
      return [];
    }

    const series: Object[] = [];
    let firstScatterSeriesColor: string | undefined;
    let firstScatterOpacity = 1;
    let secondScattereriesColor: string | undefined;

    if (lineChartData) {
      for (let i = 1; i < lineChartData[0].length; i++) {
        const rawName = lineChartData[0][i]?.toString()?.trim() || '';
        const tagId = commonKeywords ? findTagIdInKeyword(rawName, commonKeywords) : null;
        const nameWithTags = tags ? getTagLabel(tags, tagId, rawName) : rawName;
        const brandName = keywordsMapper?.get(nameWithTags) || nameWithTags;
        const isSelected =
          selectedBrand && normalizeKeyword(brandName) === normalizeKeyword(selectedBrand);
        let color: string | undefined;

        if (colorsByName) {
          color = colorsByName.find(
            (data) =>
              normalizeKeyword(data.name).toLowerCase() === normalizeKeyword(brandName).toLowerCase()
          )?.color;
        } else {
          color =
            normalizeKeyword(brandName) === normalizeKeyword(selectedBrand || '')
              ? SELECTED_BRAND_COLOR
              : firstColorPalette[i - 1];
        }

        const baseOpacity = isSelected || !selectedBrand ? 1 : 0.3;
        const seriesColorForMarks = color ?? firstColorPalette[i - 1] ?? SELECTED_BRAND_COLOR;

        if (i === 1) {
          firstScatterSeriesColor = seriesColorForMarks;
          firstScatterOpacity = baseOpacity;
        }

        let data: any;
        data = {
          type: 'line',
          smooth,
          yAxisIndex: 0,
          datasetIndex: 0,
          itemStyle: {
            color,
            opacity: baseOpacity,
          },
          lineStyle: {
            color,
            opacity: baseOpacity,
          },
          ...(selectedBrand && {
            emphasis: {
              focus: 'series',
              itemStyle: { opacity: 1 },
              lineStyle: { opacity: 1 },
            },
          }),
        } as any;

        series.push(data);
      }
    }

    if (lineChartSecondData) {
      for (let i = 1; i < lineChartSecondData[0].length; i++) {
        let color = secondColorPalette[i - 1];
        const rawName = lineChartSecondData[0][i]?.toString()?.trim() || '';
        const tagId = commonKeywords ? findTagIdInKeyword(rawName, commonKeywords) : null;
        const nameWithTags = tags ? getTagLabel(tags, tagId, rawName) : rawName;
        const brandName = keywordsMapper?.get(nameWithTags) || nameWithTags;
        if (normalizeKeyword(brandName) === normalizeKeyword(selectedBrand || '')) {
          color = SELECTED_BRAND_COLOR;
        }

        if (i === 1) {
          secondScattereriesColor = color;
        }

        series.push({
          type: 'line',
          smooth,
          yAxisIndex: 1,
          datasetIndex: 1,
          itemStyle: {
            color,
          },
          lineStyle: {
            color,
          },
        });
      }
    }

    if (firstDataEffectScatterPoints?.length && firstScatterSeriesColor) {
      series.push(
        buildScatterEffectSeries(
          firstDataEffectScatterPoints,
          0,
          firstScatterSeriesColor,
          firstScatterOpacity
        )
      );
    }

    if (secondDataEffectScatterPoints?.length && secondScattereriesColor) {
      series.push(
        buildScatterEffectSeries(secondDataEffectScatterPoints, 1, secondScattereriesColor)
      );
    }

    // Event markers (model added/deleted/released) live on a DEDICATED, always-present series so they
    // stay visible regardless of which data series the user toggles in the legend. Previously the
    // markLine was attached to the first data series, which in V2 percentageMode is the
    // (default-hidden) average-position series — so markers only appeared once that series was enabled.
    if (activeMarksData) {
      series.push({
        type: 'line',
        data: [],
        showSymbol: false,
        legendHoverLink: false,
        markLine: {
          symbol: ['circle', 'none'],
          label: { show: false },
          symbolSize: 10,
          data: getMarksData(activeMarksData, firstData),
          itemStyle: {
            color: chartColors.markLine,
          },
          lineStyle: {
            width: 1,
            color: chartColors.markLine,
          },
          emphasis: {
            lineStyle: {
              width: 1,
              opacity: 1,
            },
          },
          tooltip: {
            borderRadius: 8,
            borderColor: chartColors.tooltipBorder,
            backgroundColor: chartColors.tooltipBg,
            textStyle: {
              color: chartColors.tooltipText,
              fontSize: 12,
              lineHeight: 28,
            },
            position: 'top',
            trigger: 'item',
            axisPointer: { type: 'none' },
            formatter: function (params: any) {
              if (params.componentType === 'markLine') {
                const events = (params?.data?.events ?? []) as MonitorEventsResponse[];
                return `<div class="line-chart-tooltip__wrapper">${events
                  .map((event) => renderEventMarkHtml(event))
                  .join('<br>')}</div>`;
              }

              return '';
            },
          },
        },
      } as any);
    }

    return series;
  };

  const getMarksData = (
    marksData: MonitorEventsResponse[],
    firstData: any[][]
  ): GroupedMarkLineData[] => {
    const ranges = firstData
      .slice(1)
      .filter((row) => typeof row[0] === 'string' && row[0].trim() !== '')
      .map((row) => {
        const label = row[0].trim();
        let start: Date;
        let end: Date;

        if (label.includes(' - ')) {
          const [startStr, endStr] = label.split(' - ');
          start = parseISO(startStr);
          end = parseISO(endStr);
        } else if (label.length === 7) {
          start = parseISO(label + '-01');
          end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
        } else {
          start = parseISO(label);
          end = start;
        }

        // Match on whole calendar days: the bucket owns [start 00:00, end 23:59].
        return { label, start: startOfDay(start), end: endOfDay(end) };
      });

    // Distance from an instant to a bucket's day-range; 0 when the day falls inside it.
    const distanceToRange = (time: number, range: { start: Date; end: Date }): number => {
      if (time < range.start.getTime()) return range.start.getTime() - time;
      if (time > range.end.getTime()) return time - range.end.getTime();
      return 0;
    };

    const mapped = marksData.map((data) => {
      const created = data.created instanceof Date ? data.created : parseISO(data.created);
      const createdTime = created.getTime();

      // Assign to the bucket whose day-range contains the event; if it falls outside
      // every bucket, fall back to the nearest one so events are never dropped.
      const match =
        ranges.length > 0
          ? ranges.reduce((a, b) =>
              distanceToRange(createdTime, a) <= distanceToRange(createdTime, b) ? a : b
            )
          : undefined;

      return {
        xAxis: match?.label ?? '',
        event: data,
      };
    });

    const grouped = mapped.reduce((acc, curr) => {
      if (!acc[curr.xAxis]) {
        acc[curr.xAxis] = [];
      }
      acc[curr.xAxis].push(curr.event);
      return acc;
    }, {} as Record<string, MonitorEventsResponse[]>);

    return Object.entries(grouped).map(([xAxis, events]) => ({
      xAxis,
      events,
    }));
  };

  const chartColors = getChartThemeColors();

  const xAxisOption = {
    type: 'category',
    axisTick: {
      show: false,
    },
    axisLabel: {
      margin: 24,
      color: chartColors.axisLabel,
      ...(dateAxisFormatter && {
        formatter: (value: string) => dateAxisFormatter(value),
      }),
    },
    axisLine: {
      onZero: false,
      lineStyle: { color: chartColors.axisLine },
    },
    boundaryGap: true,
  } as any;

  const yAxisOption = {
    inverse: inverseY ?? false,
    axisLine: {
      show: true,
      onZero: false,
      lineStyle: { color: chartColors.axisLine },
    },
    splitLine: {
      lineStyle: { color: chartColors.splitLine },
    },
    axisLabel: {
      show: true,
      fontSize: 10,
      color: chartColors.axisLabel,
      padding: [0, -2, 0, 0],
      ...(formatYAsPercentage && {
        formatter: (value: number) => `${value}%`,
      }),
    },
    min: (value: { min: number }) => Math.max(minValue, Math.floor(value.min - 2)),
    max: (value: { max: number }) => {
      if (maxValue !== undefined) return maxValue;
      if (autoFitYAxis === true) {
        return Math.ceil(value.max + 2);
      }
      return isMax30 ? Math.min(30, Math.ceil(value.max + 2)) : Math.max(30, Math.ceil(value.max + 2));
    },
  } as any;

  const yAxisSecondOption = secondData
    ? ({
        inverse: inverseSecondY ?? false,
        axisLine: {
          show: true,
          lineStyle: { color: chartColors.axisLine },
        },
        splitLine: {
          lineStyle: { color: chartColors.splitLine },
        },
        axisLabel: {
          formatter: function (value: number) {
            return formatSecondYAsPercentage ? `${value}%` : formatLargeNumber(value);
          },
          fontSize: 10,
          color: chartColors.axisLabel,
          padding: [0, -2, 0, 0],
        },
        max:
          secondAxisMax !== undefined
            ? (value: { max: number }) =>
                Math.min(secondAxisMax, Math.ceil((value.max + 2) / 10) * 10)
            : (value: { max: number }) => Math.ceil((value.max + 2) / 10) * 10,
      } as any)
    : {};

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const eChartsOptions: EChartsOption = useMemo(
    () => ({
      legend: {
        orient: 'horizontal',
        top: -1000,
        right: 0,
        ...(initiallyHiddenSeries?.length
          ? { selected: Object.fromEntries(initiallyHiddenSeries.map((name) => [name, false])) }
          : {}),
      },
      tooltip: {
        trigger: 'axis',
        ...(capitalizeBrands ? { className: 'capitalize' } : {}),
        borderColor: chartColors.tooltipBorder,
        backgroundColor: chartColors.tooltipBg,
        borderRadius: 8,
        extraCssText: 'box-shadow: 0 4px 20px rgba(0,0,0,.12); overflow: hidden; padding: 0;',
        textStyle: {
          color: chartColors.tooltipText,
        },
        position: function (point, _params, _dom, _rect, size) {
          const [x, y] = point;
          const { contentSize } = size;
          const tooltipWidth = contentSize[0];
          const posX = x + 10;
          const finalPosX = posX + tooltipWidth > size.viewSize[0] ? x - tooltipWidth + 180 : posX;

          return [finalPosX, y];
        },
        formatter: function (params: any) {
          const rawDate = params[0]?.value[0];
          let dateText = dateTooltipFormatter ? dateTooltipFormatter(rawDate) : rawDate;

          if (isOverScatterRef.current && scatterTooltipParamsRef.current && effectScatterTooltipFormatter) {
            return effectScatterTooltipFormatter(scatterTooltipParamsRef.current);
          }

          let tooltipText = `<div class="line-chart-tooltip__wrapper">${dateText}<div class="line-chart-tooltip__content">`;

          const indexArray: number[] = [];
          params.forEach((param: any) => {
            const index = param.dimensionNames.findIndex((name: any) => param.seriesName === name);
            const hasEntry = indexArray.includes(index);

            if (!hasEntry) {
              indexArray.push(index);
            }

            let value = param.value[index];
            if (param.seriesName.includes(t('shareOfVoice'))) {
              value = value != null && value !== 0 ? `${value}%` : '-';
            } else if (
              param.seriesName.includes(t('brandScore')) ||
              param.seriesName.includes(t('visibility'))
            ) {
              value = value ? `${value}%` : '-';
            } else if (param.seriesName.includes(t('averagePosition'))) {
              value = value != null ? formatLargeNumber(value) : '-';
            } else if (showOutside30) {
              value = value > 30 ? t('outsideTop30') : formatLargeNumber(value);
            } else if (formatYAsPercentage) {
              value = value != null ? `${value}%` : '-';
            } else {
              value = formatLargeNumber(value);
            }

            const rawName = param.seriesName as string;
            const tagId =
              commonKeywords && findTagIdInKeyword ? findTagIdInKeyword(rawName, commonKeywords) : null;
            const nameWithTags = tags && getTagLabel ? getTagLabel(tags, tagId, rawName) : rawName;
            const mappedName = (keywordsMapper && keywordsMapper.get(nameWithTags)) || nameWithTags;

            tooltipText += `<div class="line-chart-tooltip__row"><div class="line-chart-tooltip__marker">${
              param.marker
            }</div><div class="line-chart-tooltip__text"><span>${escapeHtml(mappedName)}</span></div><strong>${
              typeof value === 'string' ? value : formatLargeNumber(value) ?? '-'
            }</strong></div>`;
          });

          tooltipText += '</div></div>';
          return tooltipText;
        },
      },
      dataset: [
        {
          source: firstData,
        },
        {
          source: secondData,
        },
      ],
      xAxis: xAxisOption,
      yAxis: [yAxisOption, yAxisSecondOption],
      grid: {
        top: '20px',
        bottom: '48px',
        left: '30px',
        right: '30px',
      },
      series: getLineSeries(firstData, secondData),
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [
      firstData,
      secondData,
      inverseY,
      inverseSecondY,
      minValue,
      maxValue,
      autoFitYAxis,
      isMax30,
      formatYAsPercentage,
      selectedBrand,
      activeMarksData,
      t,
      firstColorPalette,
      secondColorPalette,
      colorsByName,
      keywordsMapper,
      tags,
      commonKeywords,
      showOutside30,
      smooth,
      theme,
      firstDataEffectScatterPoints,
      secondDataEffectScatterPoints,
      dateAxisFormatter,
      dateTooltipFormatter,
      effectScatterTooltipFormatter,
      secondAxisMax,
      formatSecondYAsPercentage,
      initiallyHiddenSeries,
    ]
  );

  const [legendStateMap, setLegendStateMap] = useState<Map<string, boolean>>(
    () => new Map<string, boolean>(headers?.map((header) => [header, !initiallyHiddenSeries?.includes(header)]))
  );

  useEffect(() => {
    if (!headers || headers.length === 0) return;
    setLegendStateMap((prev) => {
      // Only update if new headers have keys missing from the current map
      const missingHeaders = headers.filter((h) => !prev.has(h));
      if (missingHeaders.length === 0) return prev;
      const updated = new Map(prev);
      for (const h of missingHeaders) {
        updated.set(h, !initiallyHiddenSeries?.includes(h));
      }
      return updated;
    });
  }, [headers, initiallyHiddenSeries]);

  // Keep ECharts' internal legend selection in sync with our React `legendStateMap`. A `notMerge`
  // options recompute (e.g. data refetch) wipes ECharts' selection and re-applies the static
  // `legend.selected` seed, which would desync from the custom legend — the legend item could show
  // active while the line is hidden (or vice versa). Re-applying the absolute select/unselect state
  // after each option (re)render and on every legend toggle keeps the two in lockstep.
  useEffect(() => {
    if (legendStateMap.size === 0) {
      return;
    }

    const syncLegend = (): void => {
      const chart = eChartsRef.current?.getEchartsInstance();
      if (!chart || chart.isDisposed()) {
        return;
      }

      try {
        legendStateMap.forEach((selected, name) => {
          chart.dispatchAction({ type: selected ? 'legendSelect' : 'legendUnSelect', name });
        });
      } catch {
        // Chart model not ready yet during notMerge rebuild.
      }
    };

    const chart = eChartsRef.current?.getEchartsInstance();
    if (!chart || chart.isDisposed()) {
      return;
    }

    chart.on('finished', syncLegend);
    const rafId = requestAnimationFrame(syncLegend);

    return () => {
      cancelAnimationFrame(rafId);
      if (!chart.isDisposed()) {
        chart.off('finished', syncLegend);
      }
    };
  }, [eChartsOptions, legendStateMap]);

  useEffect(() => {
    if (!onChartReady) {
      return;
    }

    const notifyReady = (): void => {
      const instance = eChartsRef.current?.getEchartsInstance();
      if (!instance || instance.isDisposed()) {
        return;
      }
      onChartReady(instance);
    };

    notifyReady();

    const instance = eChartsRef.current?.getEchartsInstance();
    if (instance && !instance.isDisposed()) {
      instance.on('finished', notifyReady);
    }
    window.addEventListener('resize', notifyReady);

    return () => {
      if (instance && !instance.isDisposed()) {
        instance.off('finished', notifyReady);
      }
      window.removeEventListener('resize', notifyReady);
    };
  }, [eChartsOptions, onChartReady]);

  useEffect(() => {
    if (!eChartsRef.current || !selectedBrand) {
      return;
    }

    const chart = eChartsRef.current.getEchartsInstance();

    const handleShowTip = () => {
      if (chart.isDisposed()) return;
      const updatedSeries = (
        Array.isArray(eChartsOptions.series) ? eChartsOptions.series : [eChartsOptions.series]
      ).map((s: any) => ({
        ...s,
        itemStyle: { ...s.itemStyle, opacity: 1 },
        lineStyle: { ...s.lineStyle, opacity: 1 },
      }));

      chart.setOption({ series: updatedSeries });
    };

    const handleHideTip = () => {
      if (chart.isDisposed()) return;
      const updatedSeries = (
        Array.isArray(eChartsOptions.series) ? eChartsOptions.series : [eChartsOptions.series]
      ).map((s: any, i: number) => {
        const index = !firstData ? null : firstData[0]?.findIndex((name: any) => selectedBrand === name);
        return {
          ...s,
          itemStyle: {
            ...s.itemStyle,
            opacity: index === i + 1 ? 1 : 0.3,
          },
          lineStyle: {
            ...s.lineStyle,
            opacity: index === i + 1 ? 1 : 0.3,
          },
        };
      });

      chart.setOption({ series: updatedSeries });
    };

    chart.on('showTip', handleShowTip);
    chart.on('hideTip', handleHideTip);

    return () => {
      // Clear any shown tooltip before the option is replaced (notMerge re-render). Without this a
      // pending ECharts `_keepShow` refresh timer can fire after the tooltip DOM is gone and crash
      // with "can't access property offsetWidth, el is null".
      if (!chart.isDisposed()) {
        chart.dispatchAction({ type: 'hideTip' });
      }
      chart.off('showTip', handleShowTip);
      chart.off('hideTip', handleHideTip);
    };
  }, [firstData, secondData, eChartsOptions, selectedBrand]);

  const handleScatterMouseOver = useCallback((params: any) => {
    if (!params.seriesName?.startsWith('__line_chart_scatter_')) {
      return;
    }
    isOverScatterRef.current = true;
    scatterTooltipParamsRef.current = params;
    const chart = eChartsRef.current?.getEchartsInstance();
    if (!chart || chart.isDisposed()) {
      return;
    }
    chart.dispatchAction({
      type: 'showTip',
      x: params.event?.offsetX,
      y: params.event?.offsetY,
    });
  }, []);

  const handleScatterMouseOut = useCallback((params: any) => {
    if (!params.seriesName?.startsWith('__line_chart_scatter_')) {
      return;
    }
    isOverScatterRef.current = false;
    scatterTooltipParamsRef.current = null;
  }, []);

  const handleGlobalOut = useCallback(() => {
    const chart = eChartsRef.current?.getEchartsInstance();
    if (!chart || chart.isDisposed()) {
      return;
    }
    // Clear any shown tooltip the moment the cursor leaves the chart. Otherwise ECharts keeps the
    // tooltip "alive" and re-arms its internal keep-show timer on each setOption; if the chart is
    // then disposed by a remount (e.g. the post-save data refresh) before that timer fires, it
    // crashes with "offsetWidth of null" in TooltipHTMLContent. Our unmount cleanup cannot prevent
    // this — React disposes the child <ReactECharts> before the parent effect cleanup runs — so we
    // defuse the timer here, while the cursor is leaving (which is exactly what happens when the
    // user moves to the save dialog).
    chart.dispatchAction({ type: 'hideTip' });
  }, []);

  const chartEvents = useMemo(
    () => ({
      mouseover: handleScatterMouseOver,
      mouseout: handleScatterMouseOut,
      globalout: handleGlobalOut,
    }),
    [handleScatterMouseOver, handleScatterMouseOut, handleGlobalOut]
  );

  const handleLegendItemClick = (label: string) => {
    const prevState = legendStateMap;
    const updatedMap = new Map(prevState);
    const currentVisibility = updatedMap.has(label) ? !updatedMap.get(label) : false;
    updatedMap.set(label, currentVisibility);

    const canManage = Array.from(updatedMap.values()).filter((value) => !value).length + 1 <= updatedMap.size;

    if (!eChartsRef.current || (!canManage && prevState.get(label) === true)) {
      return;
    }

    const instance = eChartsRef.current.getEchartsInstance();
    instance.dispatchAction({
      type: 'legendToggleSelect',
      name: label,
    });

    const firstSeriesLabels = firstData[0]?.slice(1).map(String) ?? [];
    const secondSeriesLabels = secondData?.[0]?.slice(1).map(String) ?? [];
    const scatterName = firstSeriesLabels.includes(label)
      ? '__line_chart_scatter_first__'
      : secondSeriesLabels.includes(label)
      ? '__line_chart_scatter_second__'
      : null;
    if (scatterName) {
      instance.dispatchAction({ type: 'legendToggleSelect', name: scatterName });
    }

    setLegendStateMap(updatedMap);
  };

  const getPromptByLabel = (label: string): string => {
    return headers?.find((header) => label === header) ?? label;
  };

  useEffect(() => {
    if (onLegendClick) {
      onLegendClick(legendStateMap);
    }
  }, [legendStateMap, onLegendClick]);

  const renderLegendItem = (index: number, label: string, isAveragePosition: boolean) => {
    // Map label through tags and keywordsMapper for display/color matching
    const rawName = label.trim();
    const tagId = commonKeywords ? findTagIdInKeyword(rawName, commonKeywords) : null;
    const nameWithTags = tags ? getTagLabel(tags, tagId, rawName) : rawName;
    const mappedLabel = keywordsMapper?.get(nameWithTags) || nameWithTags;

    let color;
    if (colorsByName) {
      color = colorsByName.find(
        (data) =>
          normalizeKeyword(data.name).toLowerCase() === normalizeKeyword(mappedLabel).toLowerCase()
      )?.color;
    } else {
      color = isAveragePosition ? firstColorPalette[index] : secondColorPalette[index];
      // Palette charts tint the selected brand's swatch to signal the highlight. Brand-coloured
      // charts (colorsByName) already carry each brand's fixed colour — recolouring on (hover)
      // select would flip the legend swatch mid-hover, so mirror getLineSeries and leave it be.
      if (normalizeKeyword(mappedLabel) === normalizeKeyword(selectedBrand || '')) {
        color = SELECTED_BRAND_COLOR;
      }
    }

    return (
      // Prefix the key by axis group: firstItems and secondItems are both mapped from index 0 and
      // then concatenated, so a bare `index` collides (two children with key `0`), which destabilizes
      // reconciliation and can tear down the chart DOM mid-update.
      <div
        key={`${isAveragePosition ? 'first' : 'second'}-${index}`}
        className={`${
          !legendStateMap.get(label as string) ? '' : 'line-chart__legend-item--active'
        }  line-chart__legend-item`}
        onClick={() => handleLegendItemClick(label as string)}
      >
        {renderLegendIcon ? (
          renderLegendIcon(mappedLabel as string, color)
        ) : (
          <span
            className="line-chart__legend-color"
            style={{
              background: color,
            }}
          />
        )}
        {getPromptByLabel(mappedLabel as string)}
      </div>
    );
  };

  const renderLegendItems = (): React.ReactNode[] => {
    const firstItems =
      firstData?.[0]?.slice(1).map((label, index) => renderLegendItem(index, label as string, true)) ?? [];
    const secondItems =
      secondData?.[0]?.slice(1).map((label, index) => renderLegendItem(index, label as string, false)) ?? [];
    return secondDataLegendFirst ? [...secondItems, ...firstItems] : [...firstItems, ...secondItems];
  };

  if (!firstData && !secondData) {
    return <></>;
  }

  // Przełącznik znaczników zdarzeń jako ostatnia pozycja legendy (za kreską) — osobny wiersz nad
  // wykresem dokładał kolejny rząd sterowania i zderzał się z przełącznikami widoku w rogu karty.
  const markersToggle = hasMarksData && (
    <button type="button" className="line-chart__markers-toggle" onClick={() => void toggleMarkers()}>
      <span className={`ico ${showMarkers ? 'ico--eye' : 'ico--eye-off'} ico--xs`} />
      <span>{showMarkers ? t('hideMarkers') : t('showMarkers')}</span>
    </button>
  );

  return (
    <div className="line-chart">
      {legendPosition === 'top' && (
        <div
          className={`line-chart__legend ${legendType !== 'SPACE_BETWEEN' && 'line-chart__legend--flex'}${legendSize === 'small' ? ' line-chart__legend--small' : ''}${capitalizeBrands ? ' capitalize' : ''}`}
        >
          {renderLegendItems()}
          {markersToggle}
        </div>
      )}
      <div className="line-chart__box">
        <ReactECharts
          key={theme}
          ref={eChartsRef}
          option={eChartsOptions}
          notMerge={true}
          style={chartSizeStyle}
          onEvents={chartEvents}
        />
        {showOutside30 && (
          <label className="line-chart__label line-chart__label--bottom-left">{t('outsideTop30')}</label>
        )}
        {children}
      </div>
      {legendPosition === 'bottom' && (
        <div
          className={`line-chart__legend ${legendType !== 'SPACE_BETWEEN' && 'line-chart__legend--flex'}${legendSize === 'small' ? ' line-chart__legend--small' : ''}${capitalizeBrands ? ' capitalize' : ''}`}
        >
          {renderLegendItems()}
          {markersToggle}
        </div>
      )}
    </div>
  );
};

const SCATTER_RIPPLE_EFFECT = {
  period: 2,
  scale: 3,
  brushType: 'fill' as const,
  number: 1,
};

const buildScatterEffectSeries = (
  points: LineChartEffectScatterPoint[],
  yAxisIndex: number,
  seriesColor: string,
  opacity: number = 1
): SeriesOption => ({
  type: 'effectScatter',
  name: yAxisIndex === 0 ? '__line_chart_scatter_first__' : '__line_chart_scatter_second__',
  yAxisIndex,
  xAxisIndex: 0,
  z: 10,
  data: points.map((point) => ({
    value: [point.date, point.value],
    ...(point.extra ?? {}),
  })),
  symbol: 'circle',
  symbolSize: 8,
  rippleEffect: SCATTER_RIPPLE_EFFECT,
  itemStyle: {
    color: seriesColor,
    ...(opacity < 1 ? { opacity } : {}),
  },
  tooltip: { show: false },
  emphasis: { scale: false },
});

export default LineChart;
